/**
 * AGI Quick Access - Always-accessible AGI interface panel
 */

import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  MessageSquare,
  Send,
  X,
  Sparkles,
  Code,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Zap,
} from "lucide-react";
import { agiService } from "@/services/agiService";
import { useDashboardStore } from "@/store";
import { cn } from "@/lib/utils";
import type { AGIMessage } from "@/types";

const QUICK_ACTIONS = [
  { id: "ask", label: "Ask Qwen", prompt: "", icon: Sparkles, color: "bg-purple-500" },
  { id: "code", label: "Write Code", prompt: "Generate code for:", icon: Code, color: "bg-blue-500" },
  { id: "analyze", label: "Analyze System", prompt: "Analyze current system status and provide insights", icon: RefreshCw, color: "bg-green-500" },
];

export function AGIQuickAccess() {
  const { agiPanelOpen, setAGIPanelOpen, systemMode, addLogEntry, authLevel } = useDashboardStore();
  const [messages, setMessages] = useState<AGIMessage[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [streamingContent, setStreamingContent] = useState("");

  const capabilities = agiService.getCapabilities();

  async function handleSend() {
    if (!input.trim() || isLoading) return;

    const userMessage: AGIMessage = {
      id: `msg_${Date.now()}`,
      role: "user",
      content: input,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setIsLoading(true);
    setStreamingContent("");

    // Add placeholder for streaming message
    const placeholderMessage: AGIMessage = {
      id: `msg_${Date.now() + 1}`,
      role: "assistant",
      content: "",
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, placeholderMessage]);

    // Log the interaction
    addLogEntry({
      category: "agi",
      action: "Message Sent",
      details: `User sent message to AGI in ${systemMode} mode`,
      severity: "info",
      source: "agi-quick-access",
    });

    try {
      let fullContent = "";
      const messagesBeforeUser = messages.filter(m => m !== userMessage);
      
      // Use streaming for better UX
      for await (const chunk of agiService.streamMessage(input, messagesBeforeUser)) {
        fullContent += chunk;
        setStreamingContent(fullContent);
        setMessages((prev) => {
          const updated = [...prev];
          updated[updated.length - 1] = {
            ...updated[updated.length - 1],
            content: fullContent,
          };
          return updated;
        });
      }

      setStreamingContent("");

      addLogEntry({
        category: "agi",
        action: "Message Received",
        details: `AGI responded in ${systemMode} mode (${fullContent.length} chars)`,
        severity: "info",
        source: "agi-quick-access",
      });
    } catch (error) {
      setStreamingContent("");
      // Remove placeholder on error
      setMessages((prev) => prev.slice(0, -1));
      
      const errorMessage: AGIMessage = {
        id: `msg_${Date.now() + 1}`,
        role: "system",
        content: `Error: ${error instanceof Error ? error.message : String(error)}`,
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, errorMessage]);

      addLogEntry({
        category: "agi",
        action: "Message Failed",
        details: `AGI request failed: ${error}`,
        severity: "error",
        source: "agi-quick-access",
      });
    } finally {
      setIsLoading(false);
    }
  }

  function handleQuickAction(action: typeof QUICK_ACTIONS[0]) {
    setInput(action.prompt);
    // If prompt is empty, just focus input
    if (action.prompt) {
      setTimeout(() => {
        const textarea = document.querySelector('textarea[placeholder*="Ask"]') as HTMLTextAreaElement;
        textarea?.focus();
      }, 100);
    }
  }

  function clearChat() {
    setMessages([]);
    setStreamingContent("");
    addLogEntry({
      category: "agi",
      action: "Chat Cleared",
      details: "User cleared AGI chat history",
      severity: "info",
      source: "agi-quick-access",
    });
  }

  // Floating Trigger Button
  if (!agiPanelOpen) {
    return (
      <motion.button
        onClick={() => setAGIPanelOpen(true)}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        className={cn(
          "fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full shadow-lg transition-all",
          "bg-[hsl(var(--primary))] glow-green",
          "hover:scale-105 active:scale-95"
        )}
      >
        <MessageSquare className="h-6 w-6 text-black" />
        <div className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500">
          <div className="h-2 w-2 animate-pulse rounded-full bg-white" />
        </div>
      </motion.button>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95, y: 20 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95, y: 20 }}
      className="fixed bottom-6 right-6 z-50 flex w-full max-w-lg flex-col rounded-xl border bg-[hsl(var(--background))] shadow-2xl"
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b border-[hsl(var(--border))] p-4">
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[hsl(var(--primary))]">
            <MessageSquare className="h-4 w-4 text-black" />
          </div>
          <div>
            <div className="text-sm font-semibold">AGI Quick Access</div>
            <div className="text-xs text-[hsl(var(--muted-foreground))]">
              {systemMode} Mode • {authLevel === "none" ? "Not Authenticated" : `${authLevel} Access`}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex h-8 w-8 items-center justify-center rounded-lg hover:bg-[hsl(var(--muted))] cursor-pointer"
          >
            {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          <button
            onClick={() => setAGIPanelOpen(false)}
            className="flex h-8 w-8 items-center justify-center rounded-lg hover:bg-[hsl(var(--destructive)/.2)] cursor-pointer"
          >
            <X className="h-4 w-4 text-[hsl(var(--destructive))]" />
          </button>
        </div>
      </div>

      {/* Quick Actions */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden border-b border-[hsl(var(--border))] bg-[hsl(var(--muted)/.3)]"
          >
            <div className="p-3">
              <div className="grid grid-cols-3 gap-2">
                {QUICK_ACTIONS.map((action) => {
                  const Icon = action.icon;
                  return (
                    <button
                      key={action.id}
                      onClick={() => handleQuickAction(action)}
                      disabled={!capabilities.generate}
                      className={cn(
                        "flex flex-col items-center gap-1.5 rounded-lg p-2 transition-colors cursor-pointer",
                        "hover:bg-[hsl(var(--primary)/.1)] disabled:opacity-50 disabled:cursor-not-allowed"
                      )}
                    >
                      <div
                        className={cn("flex h-8 w-8 items-center justify-center rounded-lg", action.color)}
                      >
                        <Icon className="h-4 w-4 text-white" />
                      </div>
                      <span className="text-xs font-medium">{action.label}</span>
                    </button>
                  );
                })}
              </div>

              {/* Capabilities Info */}
              <div className="mt-3 rounded-lg bg-[hsl(var(--card))] p-2">
                <div className="flex items-center gap-2">
                  <Zap className="h-3 w-3 text-[hsl(var(--primary))]" />
                  <span className="text-xs text-[hsl(var(--muted-foreground))]">Available:</span>
                  {capabilities.generate && (
                    <span className="text-xs text-[hsl(var(--foreground))]">Generate</span>
                  )}
                  {capabilities.writeFiles && (
                    <span className="text-xs text-[hsl(var(--foreground))]">• Write Files</span>
                  )}
                  {capabilities.executeActions && (
                    <span className="text-xs text-[hsl(var(--foreground))]">• Execute</span>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 max-h-96">
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <MessageSquare className="h-12 w-12 text-[hsl(var(--muted-foreground))]" />
            <div className="mt-3 text-sm font-medium">Start a conversation</div>
            <div className="mt-1 text-xs text-[hsl(var(--muted-foreground))]">
              Ask Qwen or use quick actions above
            </div>
          </div>
        ) : (
          messages.map((message) => (
            <div
              key={message.id}
              className={cn(
                "flex flex-col gap-1 max-w-[85%]",
                message.role === "user" ? "ml-auto" : "mr-auto"
              )}
            >
              <div
                className={cn(
                  "rounded-lg px-3 py-2 text-sm whitespace-pre-wrap",
                  message.role === "user"
                    ? "bg-[hsl(var(--primary))] text-black"
                    : message.role === "system"
                      ? "bg-[hsl(var(--destructive)/.2)] text-[hsl(var(--destructive))]"
                      : "bg-[hsl(var(--muted))]"
                )}
              >
                {renderMarkdown(message.content)}
              </div>
              <span className="text-[10px] text-[hsl(var(--muted-foreground))]">
                {message.timestamp.toLocaleTimeString()}
              </span>
            </div>
          ))
        )}

        {isLoading && !streamingContent && (
          <div className="flex items-center gap-2 text-xs text-[hsl(var(--muted-foreground))]">
            <div className="flex gap-1">
              <div className="h-2 w-2 animate-bounce rounded-full bg-[hsl(var(--primary))]" />
              <div className="h-2 w-2 animate-bounce rounded-full bg-[hsl(var(--primary))]" style={{ animationDelay: "0.1s" }} />
              <div className="h-2 w-2 animate-bounce rounded-full bg-[hsl(var(--primary))]" style={{ animationDelay: "0.2s" }} />
            </div>
            <span>AGI is thinking...</span>
          </div>
        )}
      </div>

      {/* Input */}
      <div className="border-t border-[hsl(var(--border))] p-4 space-y-2">
        {messages.length > 4 && (
          <button
            onClick={clearChat}
            className="self-end text-xs text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] cursor-pointer"
          >
            Clear Chat
          </button>
        )}
        <div className="flex gap-2">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), handleSend())}
            placeholder={authLevel === "none" ? "Authentication required..." : "Ask Qwen... (Enter to send)"}
            disabled={authLevel === "none" || !capabilities.generate}
            rows={1}
            className={cn(
              "flex-1 resize-none rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 py-2 text-sm",
              "focus:border-[hsl(var(--primary))] focus:outline-none focus:ring-1 focus:ring-[hsl(var(--primary))]",
              "placeholder:text-[hsl(var(--muted-foreground))]",
              "disabled:opacity-50"
            )}
          />
          <button
            onClick={handleSend}
            disabled={!input.trim() || isLoading || authLevel === "none"}
            className={cn(
              "flex h-9 w-9 items-center justify-center rounded-lg bg-[hsl(var(--primary))] transition-colors",
              "hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed",
              "cursor-pointer",
              isLoading && "animate-pulse"
            )}
          >
            {isLoading ? (
              <RefreshCw className="h-4 w-4 text-black animate-spin" />
            ) : (
              <Send className="h-4 w-4 text-black" />
            )}
          </button>
        </div>
      </div>
    </motion.div>
  );
}

/**
 * Simple markdown renderer for inline code, code blocks, and basic formatting
 */
function renderMarkdown(text: string): React.ReactNode {
  if (!text) return null;

  // Code blocks with syntax highlighting hint
  text = text.replace(/```(\w+)?\n([\s\S]*?)```/g, (match, lang, code) => {
    return `<pre class="bg-black/50 rounded-lg p-3 my-2 overflow-x-auto"><code class="text-xs text-green-400 font-mono">${escapeHtml(code.trim())}</code></pre>`;
  });

  // Inline code
  text = text.replace(/`([^`]+)`/g, '<code class="bg-black/20 px-1.5 py-0.5 rounded text-xs font-mono">$1</code>');

  // Bold
  text = text.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');

  // Italic
  text = text.replace(/\*([^*]+)\*/g, '<em>$1</em>');

  // Line breaks
  text = text.replace(/\n/g, '<br />');

  return <span dangerouslySetInnerHTML={{ __html: text }} />;
}

function escapeHtml(text: string): string {
  const div = document.createElement("div");
  div.textContent = text;
  return div.innerHTML;
}
